/**
 * Top-level data-router (createBrowserRouter).
 *
 * Public auth routes (`auth/return`, `auth/callback`, `auth/error`, `auth/signed-out`,
 * `auth/session-expired`) render as SIBLINGS of the AuthGate so they stay reachable for any
 * principal. `/auth/callback` is the same landing as `/auth/return` (invitation accept);
 * our own sign-in still sends `/auth/return`. Everything else
 * sits under the AuthGate boot guard and the reseller shell (`AppLayout`). The index route
 * redirects home (`HOME_PATH` = `/dashboard`).
 *
 * Reseller-wide (TMMRSL-0028, H §7.2): `/dashboard`, `/revenue`, `/settlements` (was `/payouts`,
 * which redirects — TMMRSL-0045), `/agreement` are TMMRSL-0031's screens; `/billing` is the
 * reseller's card on file and the charges to it (TMMRSL-0046); `/rate-cards` and
 * `/rate-cards/:id` are the price cards the platform granted and one card (TMMRSL-0074);
 * `/commission-plans` and `/commission-plans/:id` are the reseller's commission plans and one plan
 * (TMMRSL-0049); `/team` and
 * `/integrations/ghl` (OWNER only — the screen gates itself) are TMMRSL-0030's; `/account` is the
 * member's own account.
 *
 * Your business (TMMRSL-0058, MM-D-107): `/business/*` is `BusinessLayout` — Overview at the
 * index, then one route per sidebar leaf. Old `/tenants/...` and the tenant console's top-level
 * paths redirect here with `replace`.
 */
import {
  Navigate,
  createBrowserRouter,
  useLocation,
  useParams,
  type RouteObject,
} from 'react-router-dom';
import { HOME_PATH } from '@/routes/home';
import { RootLayout } from '@/routes/RootLayout';
import { RouteError } from '@/routes/RouteError';
import { AuthReturn } from '@/routes/AuthReturn';
import { AuthError } from '@/routes/AuthError';
import { SignedOut } from '@/routes/SignedOut';
import { SessionExpired } from '@/routes/SessionExpired';
import { NotFound } from '@/routes/NotFound';
import { AuthGate } from '@/auth/AuthGate';
import { AppLayout } from '@/components/AppLayout';
import { BusinessLayout } from '@/business/BusinessLayout';
import { ResellerDashboardScreen } from '@/features/dashboard/ResellerDashboardScreen';
import { RevenueScreen } from '@/features/revenue/RevenueScreen';
import { SettlementsScreen } from '@/features/payouts/SettlementsScreen';
import { AgreementScreen } from '@/features/agreement/AgreementScreen';
import { BillingScreen } from '@/features/resellerBilling/BillingScreen';
import { TenantOverview } from '@/tenant/TenantOverview';
import { RateCardsScreen } from '@/features/rateCards/RateCardsScreen';
import { RateCardScreen } from '@/features/rateCards/RateCardScreen';
import { CommissionPlansScreen } from '@/features/commissionPlans/CommissionPlansScreen';
import { CommissionPlanScreen } from '@/features/commissionPlans/CommissionPlanScreen';
import { AccountScreen } from '@/features/account/AccountScreen';
import { TeamScreen } from '@/features/team/TeamScreen';
import { GhlIntegrationScreen } from '@/features/integrations/GhlIntegrationScreen';
import { PeopleScreen } from '@/features/people/PeopleScreen';
import { SubscribersScreen } from '@/features/subscribers/SubscribersScreen';
import { NewPromotionScreen } from '@/features/promotions/NewPromotionScreen';
import { PromotionsScreen } from '@/features/promotions/PromotionsScreen';
import { StatementsScreen } from '@/features/statements/StatementsScreen';
import { AffiliatesScreen } from '@/features/affiliates/AffiliatesScreen';
import { AddonsScreen } from '@/features/addons/AddonsScreen';
import { DomainScreen } from '@/features/domain/DomainScreen';
import { SettingsScreen } from '@/features/settings/SettingsScreen';

/** The tenant console's per-tenant paths (and their detail routes) — they name no tenant. */
export const LEGACY_TENANT_PATHS = [
  'tenant-dashboard',
  'pricing',
  'subscribers',
  'subscribers/:id',
  'promotions',
  'affiliates',
  'affiliates/:id',
  'statements',
  'statements/:id',
  'add-ons',
  'domain',
  'settings',
];

/** Old `/tenants/:slug/*` → `/business/<rest>`, keeping the search string. */
export function TenantPathRedirect() {
  const params = useParams();
  const { search } = useLocation();
  const rest = params['*'] ?? '';
  const pathname = !rest || rest === 'pricing' ? '/business' : `/business/${rest}`;
  return <Navigate to={{ pathname, search }} replace />;
}

/** Old top-level tenant-console paths → `/business/<same>`, keeping `:id`. */
function LegacyTenantPathRedirect() {
  const { pathname, search } = useLocation();
  const rest = pathname.replace(/^\//, '');
  const dest =
    rest === 'tenant-dashboard' || rest === 'pricing' ? '/business' : `/business/${rest}`;
  return <Navigate to={{ pathname: dest, search }} replace />;
}

export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      { path: 'auth/return', element: <AuthReturn /> },
      { path: 'auth/callback', element: <AuthReturn /> },
      { path: 'auth/error', element: <AuthError /> },
      { path: 'auth/signed-out', element: <SignedOut /> },
      { path: 'auth/session-expired', element: <SessionExpired /> },
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to={HOME_PATH} replace /> },
              { path: 'dashboard', element: <ResellerDashboardScreen /> },
              {
                path: 'business',
                element: <BusinessLayout />,
                children: [
                  { index: true, element: <TenantOverview /> },
                  { path: 'subscribers', element: <SubscribersScreen /> },
                  { path: 'subscribers/:id', element: <SubscribersScreen /> },
                  { path: 'promotions', element: <PromotionsScreen /> },
                  { path: 'promotions/new', element: <NewPromotionScreen /> },
                  { path: 'affiliates', element: <AffiliatesScreen /> },
                  { path: 'affiliates/:id', element: <AffiliatesScreen /> },
                  { path: 'statements', element: <StatementsScreen /> },
                  { path: 'statements/:id', element: <StatementsScreen /> },
                  { path: 'add-ons', element: <AddonsScreen /> },
                  { path: 'people', element: <PeopleScreen /> },
                  { path: 'domain', element: <DomainScreen /> },
                  { path: 'settings', element: <SettingsScreen /> },
                  { path: '*', element: <NotFound /> },
                ],
              },
              { path: 'tenants', element: <Navigate to="/business" replace /> },
              { path: 'tenants/:slug/*', element: <TenantPathRedirect /> },
              { path: 'rate-cards', element: <RateCardsScreen /> },
              { path: 'rate-cards/:id', element: <RateCardScreen /> },
              { path: 'commission-plans', element: <CommissionPlansScreen /> },
              { path: 'commission-plans/:id', element: <CommissionPlanScreen /> },
              { path: 'revenue', element: <RevenueScreen /> },
              { path: 'settlements', element: <SettlementsScreen /> },
              // Old bookmarks keep working; `replace` so Back never loops (NAV-008/010).
              { path: 'payouts', element: <Navigate to="/settlements" replace /> },
              { path: 'billing', element: <BillingScreen /> },
              { path: 'agreement', element: <AgreementScreen /> },
              { path: 'team', element: <TeamScreen /> },
              { path: 'integrations/ghl', element: <GhlIntegrationScreen /> },
              { path: 'account', element: <AccountScreen /> },
              ...LEGACY_TENANT_PATHS.map((path) => ({
                path,
                element: <LegacyTenantPathRedirect />,
              })),
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(appRoutes);
